import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Ban, SquareArrowOutUpRight } from 'lucide-react';
import { Box, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { Link } from 'react-router-dom';

import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { StudentEnrolmentHealth as EnrolmentHealthEnum } from '@duke-hq/svc-api-db';

import buildLinkToEnrolments from '../../utils/buildLinkToEnrolments';
import ContentContainer from './shared/ContentContainer';
import ProgressBarCard from './shared/ProgressBarCard';
import SidePanelContentHeader from './shared/ContentHeader';
import NoDataMessage from './shared/NoDataMessage';

import PieFiftyToSeventyFive from './icons/PieFiftyToSeventyFive';
import PieZeroToTwentyFive from './icons/PieZeroToTwentyFive';
import PieTwentyFiveToFifty from './icons/PieTwentyFiveToFifty';
import PieSeventyFivePlus from './icons/PieSeventyFivePlus';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

function NoActivityCard({ count }: { count: number }) {
  return (
    <Box p={2} bg="bg.subtle" rounded="md" w="full">
      <HStack mt="1" mb="4" justify="space-between">
        <Text fontSize="xs" fontWeight="semibold" color="red.600">
          <Trans>No activity</Trans>
        </Text>
        <Link
          to={buildLinkToEnrolments({
            health: EnrolmentHealthEnum.NOT_STARTED,
          })}
          color="fg.subtle"
        >
          <SquareArrowOutUpRight size={iconSize.xs} />
        </Link>
      </HStack>

      <Flex align="center" justify="space-between" mb={5}>
        <HStack minW={pxToRem(88)} gap={1}>
          <Icon as={Ban} color="red.600" boxSize={4} />
          <Text fontSize="xs" color="fg.subtle">
            <Trans>Not started</Trans>
          </Text>
        </HStack>
        <Text fontWeight="medium" fontSize="2xs" textAlign="right" color="red.600">
          {count}
        </Text>
      </Flex>
    </Box>
  );
}

function EnrolmentHealthWithData() {
  const { filters } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getEnrolmentHealthPace.useQuery(
    { filters },
    { suspense: true },
  );

  if (data === undefined) {
    return <NoDataMessage />;
  }

  const { noActivity, behind, slipping, onTrack } = data;

  const icons = [
    <PieZeroToTwentyFive key={0} />,
    <PieTwentyFiveToFifty key={1} />,
    <PieFiftyToSeventyFive key={2} />,
    <PieSeventyFivePlus key={3} />,
  ];

  return (
    <>
      <SidePanelContentHeader
        title={t`Semester Progress`}
        tooltipText={t`Semester progress broken down by enrolment health`}
      />

      <VStack spacing={2} align="flex-start">
        <NoActivityCard count={noActivity.notStarted} />

        <ProgressBarCard
          title={t`Behind`}
          titleColor="red.600"
          subTitle={t`>2 Units behind pace`}
          subTitleColour="red.600"
          total={behind.total}
          data={behind.data}
          icons={icons}
          to={buildLinkToEnrolments({ health: EnrolmentHealthEnum.BEHIND })}
        />

        <ProgressBarCard
          title={t`Slipping`}
          titleColor="orange.500"
          subTitle={t`<2 Units behind pace`}
          subTitleColour="orange.500"
          total={slipping.total}
          data={slipping.data}
          icons={icons}
          to={buildLinkToEnrolments({ health: EnrolmentHealthEnum.SLIPPING })}
        />

        <ProgressBarCard
          title={t`On track`}
          titleColor="green.500"
          subTitle={t`<1 unit behind pace`}
          subTitleColour="green.500"
          total={onTrack.total}
          data={onTrack.data}
          icons={icons}
          to={buildLinkToEnrolments({ health: EnrolmentHealthEnum.ON_TRACK })}
        />
      </VStack>
    </>
  );
}

export default function EnrolmentHealth() {
  return (
    <ContentContainer title={t`Pace`}>
      <EnrolmentHealthWithData />
    </ContentContainer>
  );
}
